import React from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet } from 'react-native';
import { useNavigate } from 'react-router-dom';

const FeatureCard = ({ emoji, title, description }) => (
  <View style={styles.featureCard}>
    <Text style={styles.featureEmoji}>{emoji}</Text>
    <Text style={styles.featureTitle}>{title}</Text>
    <Text style={styles.featureDescription}>{description}</Text>
  </View>
);

const Welcome = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/onboarding');
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      {/* Hero Section */}
      <View style={styles.heroSection}>
        <View style={styles.logoContainer}>
          <img 
            src="/zecher-logo.png" 
            alt="Zecher"
            style={styles.logoImage}
            onError={(e) => {
              // Fallback to text logo if image fails to load
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <View style={[styles.logoFallback, { display: 'none' }]}>
            <Text style={styles.logoText}>Zecher</Text>
          </View>
        </View>
        <Text style={styles.tagline}>The Amazon of Personal Finance</Text>
        <Text style={styles.heroDescription}>
          Built from the ground up for Gen Z and Millennials. Your complete financial operating system powered by AI-driven insights.
        </Text>
        
        <TouchableOpacity style={styles.primaryButton} onPress={handleGetStarted}>
          <Text style={styles.primaryButtonText}>Get Started</Text>
        </TouchableOpacity>
      </View>

      {/* Features Section */}
      <View style={styles.featuresSection}>
        <Text style={styles.sectionTitle}>Everything you need to take control of your finances</Text>
        
        <View style={styles.featuresGrid}>
          <FeatureCard
            emoji="🏦"
            title="Smart Account Linking"
            description="Securely connect all your financial accounts in one place. Real-time balance tracking and transaction monitoring."
          />
          
          <FeatureCard
            emoji="📊"
            title="Real-Time Net Worth"
            description="See your complete financial picture instantly. Track your wealth growth and understand exactly where you stand."
          />
          
          <FeatureCard
            emoji="🤖"
            title="AI-Powered Insights"
            description="Get personalized spending insights, budget alerts, and smart recommendations to optimize your finances."
          />
          
          <FeatureCard
            emoji="🎯"
            title="Goal-Based Planning"
            description="Set financial goals and let Zecher create a personalized roadmap to achieve them faster."
          />
          
          <FeatureCard
            emoji="📈"
            title="Investment Guidance"
            description="Discover investment opportunities that align with your risk tolerance and financial goals."
          />
          
          <FeatureCard
            emoji="🛡️"
            title="Protection Planning"
            description="Ensure your financial future is protected with personalized insurance and estate planning recommendations."
          />
        </View>
      </View>

      {/* Trust Section */}
      <View style={styles.trustSection}>
        <Text style={styles.trustTitle}>Your financial data is safe with us</Text>
        <View style={styles.trustFeatures}>
          <View style={styles.trustItem}>
            <Text style={styles.trustEmoji}>🔒</Text>
            <Text style={styles.trustText}>Bank-level security</Text>
          </View>
          <View style={styles.trustItem}>
            <Text style={styles.trustEmoji}>🔐</Text>
            <Text style={styles.trustText}>256-bit encryption</Text>
          </View>
          <View style={styles.trustItem}>
            <Text style={styles.trustEmoji}>✅</Text>
            <Text style={styles.trustText}>SOC 2 compliant</Text>
          </View>
        </View>
      </View>

      {/* CTA Section */}
      <View style={styles.ctaSection}>
        <Text style={styles.ctaTitle}>Ready to transform your financial future?</Text>
        <Text style={styles.ctaDescription}>
          Join thousands who are already building wealth smarter with Zecher.
        </Text>
        
        <TouchableOpacity style={styles.primaryButton} onPress={handleGetStarted}>
          <Text style={styles.primaryButtonText}>Start Your Financial Journey</Text>
        </TouchableOpacity>
        
        <Text style={styles.ctaNote}>
          Takes less than 5 minutes to set up • No credit card required
        </Text>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  content: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  
  // Hero Section
  heroSection: {
    alignItems: 'center',
    paddingHorizontal: 24,
    marginBottom: 80,
    maxWidth: 600,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  logoImage: {
    height: 80,
    width: 'auto',
    maxWidth: 300,
    objectFit: 'contain',
  },
  logoFallback: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoText: {
    fontSize: 48,
    fontWeight: 'bold',
    color: '#2563EB',
  },
  tagline: {
    fontSize: 24,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 16,
    textAlign: 'center',
  },
  heroDescription: {
    fontSize: 18,
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: 28,
    marginBottom: 40,
  },
  primaryButton: {
    backgroundColor: '#2563EB',
    paddingHorizontal: 32,
    paddingVertical: 16,
    borderRadius: 12,
    shadowColor: '#2563EB',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '600',
  },
  
  // Features Section
  featuresSection: {
    paddingHorizontal: 24,
    marginBottom: 80,
    maxWidth: 1200,
    width: '100%',
  },
  sectionTitle: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#111827',
    textAlign: 'center',
    marginBottom: 48,
  },
  featuresGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 24,
    justifyContent: 'center',
  },
  featureCard: {
    backgroundColor: '#F9FAFB',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 32,
    alignItems: 'center',
    width: 300,
    minHeight: 200,
    // Stack on mobile
    '@media (max-width: 768px)': {
      width: '100%',
    },
  },
  featureEmoji: {
    fontSize: 48,
    marginBottom: 16,
  },
  featureTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 12,
    textAlign: 'center',
  },
  featureDescription: {
    fontSize: 16,
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: 24,
  },
  
  // Trust Section
  trustSection: {
    backgroundColor: '#F0FDF4',
    paddingHorizontal: 24,
    paddingVertical: 48,
    width: '100%',
    alignItems: 'center',
    marginBottom: 80,
  },
  trustTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 32,
    textAlign: 'center',
  },
  trustFeatures: {
    flexDirection: 'row',
    gap: 48,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  trustItem: {
    alignItems: 'center',
  },
  trustEmoji: {
    fontSize: 32,
    marginBottom: 8,
  },
  trustText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#059669',
  },
  
  // CTA Section
  ctaSection: {
    alignItems: 'center',
    paddingHorizontal: 24,
    maxWidth: 600,
  },
  ctaTitle: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#111827',
    textAlign: 'center',
    marginBottom: 16,
  },
  ctaDescription: {
    fontSize: 18,
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: 32,
    lineHeight: 28,
  },
  ctaNote: {
    fontSize: 14,
    color: '#9CA3AF',
    textAlign: 'center',
    marginTop: 16,
  },
});

export default Welcome; 